/**
 * NumberInput — labelled number input with optional unit label.
 * Single responsibility: numeric value entry with clear support.
 */

import React, { useEffect } from 'react';
import { NUMBER_INPUT_NO_NATIVE_SPIN_CLASS, ensureNoNativeSpinStyle } from './noNativeSpin';

export interface NumberInputProps {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  isDark: boolean;
}

/**
 * NumberInput component — label | spacer | number input | stepper | unit | clear.
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  unit,
  min,
  max,
  step = 1,
  isDark,
}) => {
  useEffect(() => { ensureNoNativeSpinStyle(); }, []);

  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 30,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
    flex: 1,
    whiteSpace: 'nowrap',
  };

  const inputStyle: React.CSSProperties = {
    width: 64,
    padding: '4px 6px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    textAlign: 'right',
    boxSizing: 'border-box',
    flexShrink: 0,
  };

  const stepperStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    borderRadius: 4,
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
  };

  const stepperBtnStyle: React.CSSProperties = {
    background: inputBg,
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    padding: 0,
    width: 14,
    height: 11,
    fontSize: 8,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const unitStyle: React.CSSProperties = {
    fontSize: 11,
    color: textSecondary,
    flexShrink: 0,
    minWidth: 14,
  };

  const clearBtnStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: textSecondary,
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: 13,
    lineHeight: 1,
    flexShrink: 0,
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      onChange(undefined);
    } else {
      const n = parseFloat(raw);
      if (!isNaN(n)) onChange(n);
    }
  };

  const clamp = (n: number): number => {
    let clamped = n;
    if (min !== undefined) clamped = Math.max(min, clamped);
    if (max !== undefined) clamped = Math.min(max, clamped);
    return clamped;
  };

  const bump = (delta: number) => {
    onChange(clamp((value ?? 0) + delta));
  };

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <input
        type="number"
        className={NUMBER_INPUT_NO_NATIVE_SPIN_CLASS}
        value={value === undefined ? '' : value}
        onChange={handleChange}
        min={min}
        max={max}
        step={step}
        style={inputStyle}
        aria-label={label}
      />
      <span style={stepperStyle}>
        <button
          type="button"
          style={stepperBtnStyle}
          onClick={() => bump(step)}
          aria-label={`Increase ${label}`}
          title="Increase"
        >
          ▲
        </button>
        <button
          type="button"
          style={{ ...stepperBtnStyle, borderTop: `1px solid ${borderColor}` }}
          onClick={() => bump(-step)}
          aria-label={`Decrease ${label}`}
          title="Decrease"
        >
          ▼
        </button>
      </span>
      {unit && <span style={unitStyle}>{unit}</span>}
      <button
        type="button"
        style={clearBtnStyle}
        onClick={() => onChange(undefined)}
        aria-label={`Clear ${label}`}
        title="Clear"
      >
        ×
      </button>
    </div>
  );
};

NumberInput.displayName = 'NumberInput';
